import { useEffect } from 'react';
import { LandingHeader } from '@/components/landing/LandingHeader';
import { LandingFooter } from '@/components/landing/LandingFooter';
import { ScrollToTopButton } from '@/components/landing/ScrollToTopButton';

export default function TermsOfService() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <LandingHeader />
      
      {/* Hero Section */}
      <section className="py-20 bg-primary/5">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Termos de Uso
          </h1>
          <p className="text-muted-foreground">
            Última atualização: Janeiro de 2025
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="space-y-8">
            {/* Introdução */}
            <div>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                Bem-vindo ao Dashi. Estes Termos de Uso regulam o acesso e a utilização da nossa plataforma de gestão de métricas SaaS. Ao utilizar nossos serviços, você concorda com estes termos em sua totalidade.
              </p>
            </div>

            {/* 1. Aceitação dos Termos */}
            <div>
              <h2 className="text-2xl md:text-3xl font-bold mb-4">1. Aceitação dos Termos</h2>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                Ao criar uma conta e utilizar a plataforma Dashi, você declara ter lido, compreendido e concordado com estes Termos de Uso. Se você não concordar com qualquer parte destes termos, não deverá utilizar nossos serviços.
              </p>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                Estes termos aplicam-se a todos os usuários da plataforma, incluindo visitantes, usuários cadastrados e assinantes de planos pagos.
              </p>
            </div>

            {/* 2. Descrição do Serviço */}
            <div>
              <h2 className="text-2xl md:text-3xl font-bold mb-4">2. Descrição do Serviço</h2>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                O Dashi é uma plataforma SaaS (Software as a Service) que oferece ferramentas para gestão e análise de métricas de negócios digitais, incluindo:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 text-muted-foreground mb-4">
                <li>Dashboards personalizáveis com métricas de desempenho</li>
                <li>Integrações com principais gateways de pagamento do mercado</li>
                <li>Análise de receita, churn, MRR, LTV e outras métricas relevantes</li>
                <li>Geração de relatórios detalhados em PDF e Excel</li>
                <li>Importação e exportação de dados</li>
                <li>Configuração de webhooks para sincronização automática</li>
              </ul>
            </div>

            {/* 3. Cadastro e Conta */}
            <div>
              <h2 className="text-2xl md:text-3xl font-bold mb-4">3. Cadastro e Conta</h2>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                Para utilizar nossos serviços, você deve criar uma conta fornecendo informações precisas, completas e atualizadas. Você é responsável por:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 text-muted-foreground mb-4">
                <li>Manter a confidencialidade de suas credenciais de acesso</li>
                <li>Todas as atividades que ocorram em sua conta</li>
                <li>Notificar-nos imediatamente sobre qualquer uso não autorizado</li>
                <li>Garantir que as informações cadastrais estejam sempre atualizadas</li>
              </ul>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                Você declara ter capacidade legal para celebrar contratos vinculativos. Se você estiver aceitando estes termos em nome de uma organização, você declara ter autoridade para vincular essa organização a estes termos.
              </p>
            </div>

            {/* 4. Planos e Pagamentos */}
            <div>
              <h2 className="text-2xl md:text-3xl font-bold mb-4">4. Planos e Pagamentos</h2>
              <h3 className="text-xl font-semibold mb-3 mt-6">4.1 Período de Teste</h3>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                Oferecemos um período de teste gratuito de 14 dias para novos usuários. Durante este período, você terá acesso a todas as funcionalidades do plano escolhido. Não é necessário fornecer informações de pagamento para iniciar o teste.
              </p>
              
              <h3 className="text-xl font-semibold mb-3 mt-6">4.2 Planos Pagos</h3>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                Após o período de teste, você poderá escolher entre nossos planos pagos. Os valores e funcionalidades de cada plano estão descritos em nossa página de preços. Os pagamentos são processados mensalmente ou anualmente, conforme sua escolha.
              </p>
              
              <h3 className="text-xl font-semibold mb-3 mt-6">4.3 Renovação Automática</h3>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                As assinaturas são renovadas automaticamente no final de cada período de cobrança, salvo se canceladas antes da data de renovação. Você será cobrado no mesmo método de pagamento utilizado anteriormente.
              </p>
              
              <h3 className="text-xl font-semibold mb-3 mt-6">4.4 Política de Reembolso</h3>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                Não oferecemos reembolsos para períodos já faturados. Ao cancelar sua assinatura, você manterá acesso aos recursos pagos até o final do período de cobrança vigente.
              </p>
            </div>

            {/* 5. Uso Aceitável */}
            <div>
              <h2 className="text-2xl md:text-3xl font-bold mb-4">5. Uso Aceitável</h2>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                Ao utilizar o Dashi, você concorda em NÃO:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 text-muted-foreground mb-4">
                <li>Utilizar a plataforma para atividades ilegais ou fraudulentas</li>
                <li>Tentar obter acesso não autorizado a sistemas, dados ou redes</li>
                <li>Fazer engenharia reversa, descompilar ou desmontar qualquer parte do serviço</li>
                <li>Enviar spam, vírus, malware ou qualquer código malicioso</li>
                <li>Interferir ou interromper a integridade ou desempenho da plataforma</li>
                <li>Coletar dados de outros usuários sem consentimento</li>
                <li>Revender, sublicenciar ou transferir seus direitos de acesso</li>
                <li>Utilizar a plataforma de forma que viole leis ou regulamentos aplicáveis</li>
              </ul>
            </div>

            {/* 6. Propriedade Intelectual */}
            <div>
              <h2 className="text-2xl md:text-3xl font-bold mb-4">6. Propriedade Intelectual</h2>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                O Dashi e todo o seu conteúdo, recursos e funcionalidades (incluindo, mas não se limitando a, software, texto, gráficos, logos, ícones e imagens) são de propriedade exclusiva do Dashi e estão protegidos por leis de direitos autorais, marcas registradas e outras leis de propriedade intelectual.
              </p>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                Concedemos a você uma licença limitada, não exclusiva, intransferível e revogável para acessar e usar a plataforma de acordo com estes termos.
              </p>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                Você mantém todos os direitos sobre os dados que você carrega, processa ou armazena na plataforma. Ao usar nossos serviços, você nos concede uma licença para processar esses dados conforme necessário para fornecer os serviços.
              </p>
            </div>

            {/* 7. Limitação de Responsabilidade */}
            <div>
              <h2 className="text-2xl md:text-3xl font-bold mb-4">7. Limitação de Responsabilidade</h2>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                O serviço é fornecido "como está" e "conforme disponível", sem garantias de qualquer tipo, expressas ou implícitas. O Dashi não garante que:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 text-muted-foreground mb-4">
                <li>O serviço estará disponível ininterruptamente ou sem erros</li>
                <li>Os defeitos serão corrigidos</li>
                <li>O serviço estará livre de vírus ou outros componentes prejudiciais</li>
                <li>Os resultados obtidos pelo uso do serviço serão precisos ou confiáveis</li>
              </ul>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                Em nenhum caso o Dashi será responsável por danos indiretos, incidentais, especiais, consequenciais ou punitivos, incluindo perda de lucros, dados, uso ou outras perdas intangíveis.
              </p>
            </div>

            {/* 8. Modificações nos Termos */}
            <div>
              <h2 className="text-2xl md:text-3xl font-bold mb-4">8. Modificações nos Termos</h2>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                Reservamo-nos o direito de modificar estes Termos de Uso a qualquer momento. Notificaremos você sobre alterações significativas por email ou através de um aviso em nossa plataforma. O uso continuado dos serviços após tais alterações constitui aceitação dos novos termos.
              </p>
            </div>

            {/* 9. Cancelamento e Rescisão */}
            <div>
              <h2 className="text-2xl md:text-3xl font-bold mb-4">9. Cancelamento e Rescisão</h2>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                Você pode cancelar sua assinatura a qualquer momento através das configurações de conta. O cancelamento terá efeito ao final do período de cobrança atual.
              </p>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                Reservamos o direito de suspender ou encerrar sua conta imediatamente, sem aviso prévio, por violação destes termos ou por qualquer conduta que consideremos prejudicial à plataforma ou a outros usuários.
              </p>
            </div>

            {/* 10. Lei Aplicável */}
            <div>
              <h2 className="text-2xl md:text-3xl font-bold mb-4">10. Lei Aplicável</h2>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                Estes Termos de Uso são regidos pelas leis da República Federativa do Brasil. Qualquer disputa decorrente destes termos será submetida ao foro da comarca de São Paulo, SP, com exclusão de qualquer outro, por mais privilegiado que seja.
              </p>
            </div>

            {/* 11. Contato */}
            <div>
              <h2 className="text-2xl md:text-3xl font-bold mb-4">11. Contato</h2>
              <p className="text-muted-foreground mb-4 leading-relaxed">
                Se você tiver dúvidas sobre estes Termos de Uso, entre em contato conosco através do email: contato@dashi.com.br
              </p>
            </div>

            {/* Footer de última atualização */}
            <div className="pt-8 border-t">
              <p className="text-sm text-muted-foreground text-center">
                © {new Date().getFullYear()} Dashi. Todos os direitos reservados.
              </p>
            </div>
          </div>
        </div>
      </section>

      <LandingFooter />
      <ScrollToTopButton />
    </div>
  );
}
